import React, { useState, useEffect } from 'react';
import { Layers, Plus, Edit2, Shield, AlertTriangle } from 'lucide-react';
import api from '../../services/api';

const VerificationLevels = () => {
  const [levels, setLevels] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    level_tier: 1,
    required_confidence_score: 0.70,
    enabled: true,
    risk_level: 'MEDIUM',
    minimum_sources_count: 1,
    allow_partial_match: false,
    require_biometric: false,
    require_liveness_check: false,
    color: 'blue',
    display_order: 0
  });
  const [selectedSources, setSelectedSources] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [levelsRes, sourcesRes] = await Promise.all([
        api.get('/verification-levels'),
        api.get('/verification-sources')
      ]);
      setLevels(levelsRes.data.levels || []);
      setSources(sourcesRes.data.sources || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        sources: selectedSources
      };
      
      if (editingLevel) {
        await api.put(`/verification-levels/${editingLevel.id}`, payload);
      } else {
        await api.post('/verification-levels', payload);
      }
      setShowModal(false);
      setEditingLevel(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving level:', error);
      alert('Failed to save verification level');
    }
  };

  const handleEdit = (level) => {
    setEditingLevel(level);
    setFormData({
      name: level.name,
      slug: level.slug,
      description: level.description,
      level_tier: level.level_tier,
      required_confidence_score: level.required_confidence_score,
      enabled: level.enabled,
      risk_level: level.risk_level,
      minimum_sources_count: level.minimum_sources_count,
      allow_partial_match: level.allow_partial_match,
      require_biometric: level.require_biometric,
      require_liveness_check: level.require_liveness_check,
      color: level.color || 'blue',
      display_order: level.display_order
    });
    setSelectedSources(level.sources?.map(s => ({ id: s.id, is_required: s.is_required, weight: s.weight })) || []);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      level_tier: 1,
      required_confidence_score: 0.70,
      enabled: true,
      risk_level: 'MEDIUM',
      minimum_sources_count: 1,
      allow_partial_match: false,
      require_biometric: false,
      require_liveness_check: false,
      color: 'blue',
      display_order: 0
    });
    setSelectedSources([]);
  };

  const toggleSource = (sourceId) => {
    const exists = selectedSources.find(s => s.id === sourceId);
    if (exists) {
      setSelectedSources(selectedSources.filter(s => s.id !== sourceId));
    } else {
      setSelectedSources([...selectedSources, { id: sourceId, is_required: false, weight: 1.00 }]);
    }
  };

  const updateSourceConfig = (sourceId, field, value) => {
    setSelectedSources(selectedSources.map(s => 
      s.id === sourceId ? { ...s, [field]: value } : s
    ));
  };

  const getRiskColor = (risk) => {
    const colors = {
      LOW: 'bg-green-100 text-green-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      HIGH: 'bg-orange-100 text-orange-800',
      CRITICAL: 'bg-red-100 text-red-800'
    };
    return colors[risk] || colors.MEDIUM;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading verification levels...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Verification Levels</h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure verification levels with required sources and confidence thresholds
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingLevel(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Level
        </button>
      </div>

      {/* Levels List */}
      <div className="space-y-4">
        {levels.map((level) => (
          <div
            key={level.id}
            className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Layers className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">{level.name}</h3>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                    Tier {level.level_tier}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getRiskColor(level.risk_level)}`}>
                    {level.risk_level} Risk
                  </span>
                  {!level.enabled && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                      Disabled
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-4">{level.description}</p>
                
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-gray-500">Required Confidence</div>
                    <div className="text-sm font-medium text-gray-900">
                      {(parseFloat(level.required_confidence_score) * 100).toFixed(0)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Min Sources</div>
                    <div className="text-sm font-medium text-gray-900">{level.minimum_sources_count}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Biometric</div>
                    <div className="text-sm font-medium text-gray-900">
                      {level.require_biometric ? 'Required' : 'Optional'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Liveness Check</div>
                    <div className="text-sm font-medium text-gray-900">
                      {level.require_liveness_check ? 'Required' : 'Optional'}
                    </div>
                  </div>
                </div>

                {level.sources && level.sources.length > 0 && (
                  <div>
                    <div className="text-xs text-gray-500 mb-2">Associated Sources:</div>
                    <div className="flex flex-wrap gap-2">
                      {level.sources.map((source) => (
                        <span
                          key={source.id}
                          className={`px-2 py-1 text-xs rounded ${
                            source.is_required
                              ? 'bg-red-100 text-red-800 font-medium'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {source.name} {source.is_required && '(Required)'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => handleEdit(level)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>

      {levels.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No verification levels configured</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingLevel ? 'Edit Verification Level' : 'Add Verification Level'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                />
              </div>

              {/* Level Configuration */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tier (1-10)</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.level_tier}
                    onChange={(e) => setFormData({ ...formData, level_tier: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confidence Score</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={formData.required_confidence_score}
                    onChange={(e) => setFormData({ ...formData, required_confidence_score: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label>
                  <select
                    value={formData.risk_level}
                    onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Sources</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.minimum_sources_count}
                    onChange={(e) => setFormData({ ...formData, minimum_sources_count: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Enabled</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.allow_partial_match}
                    onChange={(e) => setFormData({ ...formData, allow_partial_match: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Allow Partial Match</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.require_biometric}
                    onChange={(e) => setFormData({ ...formData, require_biometric: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Require Biometric</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.require_liveness_check}
                    onChange={(e) => setFormData({ ...formData, require_liveness_check: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Require Liveness Check</span>
                </label>
              </div>

              {/* Sources Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Verification Sources</label>
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4">
                  {sources.filter(s => s.enabled).map((source) => {
                    const selected = selectedSources.find(s => s.id === source.id);
                    return (
                      <div key={source.id} className="flex items-center gap-4 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={!!selected}
                          onChange={() => toggleSource(source.id)}
                          className="rounded"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900">{source.name}</div>
                          <div className="text-xs text-gray-500">
                            {source.source_type} • Confidence: {(parseFloat(source.base_confidence_score) * 100).toFixed(0)}%
                          </div>
                        </div>
                        {selected && (
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={selected.is_required}
                                onChange={(e) => updateSourceConfig(source.id, 'is_required', e.target.checked)}
                                className="rounded"
                              />
                              <span className="text-xs text-gray-600">Required</span>
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              min="0"
                              max="2"
                              value={selected.weight}
                              onChange={(e) => updateSourceConfig(source.id, 'weight', parseFloat(e.target.value))}
                              className="w-16 px-2 py-1 text-xs border border-gray-300 rounded"
                              placeholder="Weight"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingLevel(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingLevel ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationLevels;

